import {Component, Input, TemplateRef} from '@angular/core';
import {MenuService} from "../../services/menu-service";
import {BizService} from "../../services/biz.service";
import { LanguageService } from 'src/app/services/language.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
    selector: 'app-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.css'],
    standalone: false
})
export class MenuComponent {

  leftMenuItems: MenuItem[] = [
    { itemName: "Dispositivi",
      items: [
        {itemName: "Lista Dispositivi" },
        {itemName: "Gestione Dispositivi" },
        {itemName: "Storico" }
      ]
    }
  ];

  rightMenuItems: MenuItem[] = [
    { itemName: "Utente",
      items: [
        {itemName: "Scollegamento Utente", onClick: () => this.logout(), allowedWhenUserDisabled: true },
        {itemName: "Cambio Password" },
        {itemName: "Elimina Account" }
      ]
    },
    { itemName: "Impostazioni",
      items: [
        {itemName: "Gestione Utenti" },
        this.getLanguageMenuItem("English", 'en'),
        this.getLanguageMenuItem("Italiano", 'it')
      ]
    }
  ];

  getLanguageMenuItem(name: string, key: string) : MenuItem {
    return {
      itemName: name,
      onClick: () => this.switchLang(key),
      iconSource: `../../../assets/img/${key}.png`,
      isPublic: true,
      isNoMenu: true,
      allowedWhenUserDisabled: true
    }
  }

  constructor(private menuService: MenuService,
              private myBiz: BizService,
              private languageService: LanguageService,
              private utility: UtilityService)
  {
    this.buildMenuIndex(this.menuMetadata);
  }

  switchLang(lang: string) {
    this.languageService.setLanguage(lang);
  }

  HasPermission(menuName: string): boolean {
    return this.menuService.IsUserEnabled(menuName);
  }

  getMenuTitle(itemName: string): string {
    const text = this.menuService.GetMenuTitle(itemName);
    return this.languageService.getTranslationMenu(text);
  }

  getMenuTitleByItem(item: MenuItem): string {    
    const metadata = this.findMenuItem(item.itemName);
    if (!metadata)
      return item.itemName;

    if (metadata.isNoMenu)
      return metadata.itemName;

    return this.getMenuTitle(metadata.itemName);
  }

  getMenuPageTitle(menuName: string): string {
    const text = this.menuService.GetMenuPageTitle(menuName);
    return this.languageService.getTranslationMenu(text);
  }

  setActiveMenuName(menuName: string): void {
    this.menuService.SetActiveMenuName(menuName);
    this.menuService.ShowLoadingInProgressScreen();
  }

  getActiveMenuTitle(): string {
    const text = this.menuService.GetMenuTitle(this.menuService.GetActiveMenuName());
    return this.languageService.getTranslationMenu(text);
  }

  getActiveMenuPageTitle(): string {
    const text = this.menuService.GetMenuPageTitle(this.menuService.GetActiveMenuName());
    return this.languageService.getTranslationMenu(text);
  }

  logout() {
    this.myBiz.UserLogout();
    this.setActiveMenuName('Account - User Login');
  }

  isHighlighted(item: MenuItem) {
    if (!item.iconSource)
      return false;

    const currentLanguage = this.languageService.getCurrentLang();
    return item.iconSource.endsWith(`${ currentLanguage}.png`);
  }

  UserIsEnabled() : boolean
  {
    return this.utility.GetUserIsEnabled();
  }

  // This hierarchy object was added here because IsVisible needs to know the children of any item.
  // This cannot also be used to build the menu because the referenced containers
  // (e.g. <mat-menu #my_reference="matMenu">) need to be explicitly defined in the .html file.
  readonly menuMetadata = [
    {
      itemName: 'Utente',
      items: [
        { itemName: 'Scollegamento Utente', allowedWhenUserDisabled: true },
        { itemName: 'Cambio Password' },
        { itemName: 'Elimina Account' }
      ]
    },
    {
      itemName: 'Impostazioni',
      items: [
        { itemName: 'Gestione Utenti' },
        { itemName: 'English', allowedWhenUserDisabled: true, isPublic: true, isNoMenu: true },
        { itemName: 'Italiano', allowedWhenUserDisabled: true, isPublic: true, isNoMenu: true }
      ]
    }
  ];

  private readonly menuIndex = new Map<string, MenuItem>();

  private buildMenuIndex(items: MenuItem[]) {
    for (const item of items) {
      this.menuIndex.set(item.itemName, item);

      if (item.items) {
        this.buildMenuIndex(item.items);
      }
    }
  }

  findMenuItem(itemName: string): MenuItem | undefined {
    return this.menuIndex.get(itemName);
  }

  IsVisible(inputItem: MenuItem) : boolean
  {
    const metadata = this.findMenuItem(inputItem.itemName);

    const item: MenuItem = {
      ...metadata,
      ...inputItem
    };

    if (item.isPublic)
      return true;

    if (!this.HasPermission(item.itemName) && ! item.isPublic)
      return false;

    const itemMetadata = this.findMenuItem(item.itemName);
    if (itemMetadata)
    {
      const anyChildVisible = itemMetadata.items?.some(subItem => this.IsVisible(subItem));
      if (anyChildVisible)
        return true;
    }    

    if (item.allowedWhenUserDisabled !== undefined && item.allowedWhenUserDisabled === true)
      return true;

    return this.UserIsEnabled();
  }
}

interface MenuItem {
  iconSource?: string;
  isPublic?: boolean;
  isNoMenu?: boolean;
  itemName: string;
  items?: MenuItem[];
  onClick?: () => void;
  allowedWhenUserDisabled?: boolean;
}
